// src/hooks/usePermissions.ts
import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

export type Permission =
  | 'user_management'
  | 'dashboard'
  | 'industries'
  | 'marketplace'
  | 'content_management'
  | 'admin_management'
  | 'home_content'
  | 'support_messages';

export const usePermissions = () => {
  const { user } = useAuth();

  const permissions = useMemo(() => {
    if (!user || user.userType !== 'admin') {
      return {
        hasPermission: () => false,
        isSuperAdmin: false,
        permissions: []
      };
    }

    // Super admins have all permissions
    if (user.isSuperAdmin) {
      return {
        hasPermission: () => true,
        isSuperAdmin: true,
        permissions: ['all'] as string[]
      };
    }

    // Sub-admins only have their granted permissions
    const userPermissions = user.permissions || [];

    return {
      hasPermission: (permission: Permission | Permission[]) => {
        if (Array.isArray(permission)) {
          return permission.some(p => userPermissions.includes(p));
        }
        return userPermissions.includes(permission);
      },
      isSuperAdmin: false,
      permissions: userPermissions
    };
  }, [user]);

  return permissions;
};
